import { useState } from "react";
import axios from "axios";
import { MessageCircle, Phone, MapPin, Car, Home, Truck } from "lucide-react";
import { useLang } from "../i18n";
import { SERVICES, ZONES, waQuote, PHONE_DISPLAY, WA_LINK } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = ["sizeS", "sizeM", "sizeXL", "notSure"];

export default function ContactForm({ compact = false }) {
  const { lang, t } = useLang();
  const [form, setForm] = useState({ name: "", phone: "", model: "", service: "", size: "", locality: "", message: "" });

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    const svc = SERVICES.find((s) => s.slug === form.service);
    const svcName = svc ? (svc[lang]?.name || svc.es.name) : form.service;
    const sizeLabel = form.size ? t(`contact.${form.size}`) : "";
    const lines = [
      lang === "ca" ? "Hola EcoGlow! Vull demanar pressupost." : "¡Hola EcoGlow! Quiero pedir presupuesto.",
      `${t("contact.name")}: ${form.name}`,
      form.phone && `${t("contact.phone")}: ${form.phone}`,
      form.model && `${t("contact.model")}: ${form.model}`,
      svcName && `${t("contact.service")}: ${svcName}`,
      sizeLabel && `${t("contact.size")}: ${sizeLabel}`,
      form.locality && `${t("contact.locality")}: ${form.locality}`,
      form.message && `${t("contact.message")}: ${form.message}`,
    ].filter(Boolean);
    axios.post(`${API}/leads`, { ...form, service: svcName || form.service, lang }).catch(() => {});
    window.open(waQuote(lines.join("\n")), "_blank", "noopener");
  };

  const inputCls =
    "w-full rounded-lg border border-white/10 bg-[#0d0d0d] px-4 py-3 text-sm text-white placeholder-zinc-500 outline-none transition-colors focus:border-[#63E51D] focus:ring-1 focus:ring-[#63E51D]";

  return (
    <div className={compact ? "" : "grid gap-10 lg:grid-cols-5"}>
      {!compact && (
        <div className="space-y-6 lg:col-span-2">
          <a href={`tel:${PHONE_DISPLAY.replace(/\s/g, "")}`} data-testid="contact-call-button" className="flex items-center gap-4 rounded-xl border border-white/5 bg-[#121212] p-5 transition-colors hover:border-[#63E51D]/40">
            <span className="rounded-full bg-[#63E51D]/10 p-3 text-[#63E51D]"><Phone className="h-5 w-5" /></span>
            <div>
              <p className="text-sm text-zinc-400">{t("contact.call")}</p>
              <p className="font-display text-xl font-bold text-white">{PHONE_DISPLAY}</p>
            </div>
          </a>
          <a href={WA_LINK} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button" className="flex items-center gap-4 rounded-xl border border-white/5 bg-[#121212] p-5 transition-colors hover:border-[#63E51D]/40">
            <span className="rounded-full bg-[#63E51D]/10 p-3 text-[#63E51D]"><MessageCircle className="h-5 w-5" /></span>
            <div>
              <p className="text-sm text-zinc-400">WhatsApp</p>
              <p className="font-display text-xl font-bold text-white">{PHONE_DISPLAY}</p>
            </div>
          </a>
          <div className="space-y-3 rounded-xl border border-white/5 bg-[#121212] p-5">
            {[
              { icon: MapPin, text: t("contact.infoCenter") },
              { icon: Truck, text: t("contact.infoPickup") },
              { icon: Home, text: t("contact.infoHome") },
            ].map((r, i) => (
              <p key={i} className="flex items-start gap-3 text-sm text-zinc-300">
                <r.icon className="mt-0.5 h-4 w-4 shrink-0 text-[#63E51D]" /> {r.text}
              </p>
            ))}
          </div>
        </div>
      )}

      <form onSubmit={submit} data-testid="contact-form" className={compact ? "" : "rounded-2xl border border-white/5 bg-[#121212] p-6 sm:p-8 lg:col-span-3"}>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label htmlFor="cf-name" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-zinc-400">{t("contact.name")} *</label>
            <input id="cf-name" data-testid="contact-name-input" required value={form.name} onChange={set("name")} className={inputCls} placeholder={lang === "ca" ? "El teu nom" : "Tu nombre"} />
          </div>
          <div>
            <label htmlFor="cf-phone" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-zinc-400">{t("contact.phone")}</label>
            <input id="cf-phone" data-testid="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="600 000 000" />
          </div>
          <div>
            <label htmlFor="cf-model" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-zinc-400">{t("contact.model")}</label>
            <input id="cf-model" data-testid="contact-model-input" value={form.model} onChange={set("model")} className={inputCls} placeholder="BMW 320d" />
          </div>
          <div>
            <label htmlFor="cf-service" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-zinc-400">{t("contact.service")}</label>
            <select id="cf-service" data-testid="contact-service-select" value={form.service} onChange={set("service")} className={inputCls}>
              <option value="">{t("contact.selectService")}</option>
              {SERVICES.map((s) => (
                <option key={s.slug} value={s.slug}>{s[lang]?.name || s.es.name}</option>
              ))}
              <option value="pack">{lang === "ca" ? "Pack (Escut Total, etc.)" : "Pack (Escudo Total, etc.)"}</option>
            </select>
          </div>
          <div>
            <label htmlFor="cf-size" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-zinc-400">{t("contact.size")}</label>
            <select id="cf-size" data-testid="contact-size-select" value={form.size} onChange={set("size")} className={inputCls}>
              <option value="">{t("contact.selectSize")}</option>
              {SIZES.map((s) => (
                <option key={s} value={s}>{t(`contact.${s}`)}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="cf-locality" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-zinc-400">{t("contact.locality")}</label>
            <select id="cf-locality" data-testid="contact-locality-select" value={form.locality} onChange={set("locality")} className={inputCls}>
              <option value="">{lang === "ca" ? "La teva localitat" : "Tu localidad"}</option>
              {ZONES.map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
              <option value="other">{lang === "ca" ? "Una altra" : "Otra"}</option>
            </select>
          </div>
          <div className="sm:col-span-2">
            <label htmlFor="cf-message" className="mb-1.5 block text-xs font-medium uppercase tracking-wider text-zinc-400">{t("contact.message")}</label>
            <textarea id="cf-message" data-testid="contact-message-input" rows={4} value={form.message} onChange={set("message")} className={inputCls} placeholder={lang === "ca" ? "Explica'ns què necessita el teu cotxe…" : "Cuéntanos qué necesita tu coche…"} />
          </div>
        </div>
        <button
          type="submit"
          data-testid="contact-submit-button"
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#63E51D] px-6 py-4 font-display text-lg font-bold uppercase tracking-wide text-black transition-all duration-300 hover:scale-[1.01] hover:shadow-[0_0_25px_rgba(99,229,29,0.4)] active:scale-[0.98]"
        >
          <MessageCircle className="h-5 w-5" /> {t("contact.send")}
        </button>
        <p className="mt-3 flex items-center justify-center gap-2 text-center text-xs text-zinc-500">
          <Car className="h-3.5 w-3.5" /> {t("contact.sent")}
        </p>
      </form>
    </div>
  );
}
